import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * 轉錄片段（帶時間碼）
 */
export interface TranscriptionSegment {
  id: number;
  start: number;
  end: number;
  text: string;
}

/**
 * 轉錄結果
 */
export interface TranscriptionResult {
  text: string;
  segments: TranscriptionSegment[];
  language: string;
  duration: number;
}

/**
 * 轉錄狀態
 */
export type TranscriptionStatus = 'idle' | 'uploading' | 'transcribing' | 'done' | 'error';

/**
 * 語音轉錄 Hook
 * 調用 Supabase Edge Function (whisper-transcribe) 進行語音轉文字
 */
export function useTranscription() {
  const [status, setStatus] = useState<TranscriptionStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<TranscriptionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * 執行轉錄
   * @param audioBlob 音頻 Blob
   * @param language 語言代碼 (默認 'zh')
   * @param recordingId 錄音 ID（可選，用於權限驗證）
   * @param prompt 提示詞（可選，輸入原始腳本內容可顯著提升時間戳精確度）
   * @returns 轉錄結果或 null
   */
  const transcribe = useCallback(async (
    audioBlob: Blob,
    language: string = 'zh',
    recordingId?: string,
    prompt?: string
  ): Promise<TranscriptionResult | null> => {
    setStatus('uploading');
    setProgress(0);
    setError(null);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('未登入');
      }

      // 構建 FormData
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', language);
      if (recordingId) {
        formData.append('recording_id', recordingId);
      }
      if (prompt) {
        formData.append('prompt', prompt);
      }

      setStatus('transcribing');
      setProgress(30);

      // 從 supabase client 獲取 URL 和 anon key
      // @ts-expect-error - accessing internal supabase properties
      const supabaseUrl = supabase.supabaseUrl;
      // @ts-expect-error - accessing internal supabase properties  
      const supabaseKey = supabase.supabaseKey;

      // 調用 Edge Function - 使用原生 fetch，必須同時傳遞 Authorization 和 apikey
      const response = await fetch(
        `${supabaseUrl}/functions/v1/whisper-transcribe`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': supabaseKey,
          },
          body: formData,
        }
      );

      setProgress(80);

      if (!response.ok) {
        let errorMessage = '轉錄失敗';
        try {
          const errorData = await response.json();
          if (errorData?.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // response might not be JSON
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      // 轉換為標準格式
      const transcriptionResult: TranscriptionResult = {
        text: data.text,
        segments: data.segments?.map((seg: { start: number; end: number; text: string }, index: number) => ({
          id: index + 1,
          start: seg.start,
          end: seg.end,
          text: seg.text.trim(),
        })) || [],
        language: data.language,
        duration: data.duration,
      };

      setResult(transcriptionResult);
      setStatus('done');
      setProgress(100);
      
      return transcriptionResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : '未知錯誤';
      setError(message);
      setStatus('error');
      return null;
    }
  }, []);

  /**
   * 重置狀態
   */
  const reset = useCallback(() => {
    setStatus('idle');
    setProgress(0);
    setResult(null);
    setError(null);
  }, []);

  return { 
    status, 
    progress, 
    result, 
    error, 
    transcribe, 
    reset,
    isTranscribing: status === 'uploading' || status === 'transcribing',
  };
}
